import type MultiSelect from '../index';

export class TagsLayout {
    private ms: MultiSelect;

    constructor(multiSelect: MultiSelect) {
        this.ms = multiSelect;
    }

    private createElement<K extends keyof HTMLElementTagNameMap>(
        tag: K,
        className?: string,
        html: string = ''
    ): HTMLElementTagNameMap[K] {
        const el = document.createElement(tag);

        if (className) {
            el.className = className;
        }

        if (html) {
            el.innerHTML = html;
        }

        return el;
    }

    private renderOption(option: HTMLOptionElement, index: number): string {
        const { ms } = this;
        const value = option.value;
        const label = option.text;
        const isSelected = option.selected;
        const uniqueId = `${ms.wrapper.id}-option-${index}`;

        if (ms.isMultiple && ms.showCheckboxes) {
            return `
                <div class="form-check" data-value="${value}">
                    <input class="form-check-input" type="checkbox" value="${value}" id="${uniqueId}" ${isSelected ? 'checked' : ''}>
                    <label class="form-check-label" for="${uniqueId}">${label}</label>
                </div>
            `;
        } else {
            return `
                <div class="form-check no-checkbox" data-value="${value}">
                    <label class="form-check-label" for="${uniqueId}">${label}</label>
                </div>
            `;
        }
    }

    public init(): void {
        const { ms } = this;

        ms.toggle = this.createElement('div', 'select-toggle');
        ms.toggle.tabIndex = 0;

        ms.displayText = this.createElement('span', 'select-display-text', ms.placeholder);
        ms.menu = this.createElement('div', 'select-menu');
        ms.menuBody = this.createElement('div', 'select-menu-body');

        const tagsTargetSelector = ms.select.dataset.tagsTarget;
        ms.tagsContainer = tagsTargetSelector
            ? document.querySelector<HTMLElement>(tagsTargetSelector) || this.createElement('div', 'selected-tags-container')
            : this.createElement('div', 'selected-tags-container');

        if (ms.isSortable) {
            ms.tagsContainer.classList.add('sortable-tags');
        }

        ms.toggle.appendChild(ms.displayText);
        ms.toggle.appendChild(this.createElement('span', 'dropdown-toggle-icon'));
        ms.wrapper.appendChild(ms.toggle);
        ms.wrapper.appendChild(ms.menu);

        if (ms.isMultiple && ms.showTags) {
            if (!tagsTargetSelector) {
                ms.wrapper.appendChild(ms.tagsContainer);
            } else {
                ms.wrapper.classList.add('has-custom-tags');
            }
        }
    }

    public renderMenu(): void {
        const { ms } = this;

        if (ms.isSearchable) {
            ms.menu.innerHTML = ms.search.createMenuSearchHtml();
            ms.search.initFromContainer(ms.menu);
        }

        ms.menu.appendChild(ms.menuBody);

        const optionsList = this.createElement('div', 'options-list');
        let optionIndex = 0;
        const optgroups = ms.select.querySelectorAll('optgroup');

        if (optgroups.length > 0) {
            optgroups.forEach((optgroup) => {
                const groupLabel = optgroup.label;
                const groupHeader = this.createElement('div', 'optgroup-header', groupLabel);
                optionsList.appendChild(groupHeader);

                Array.from(optgroup.querySelectorAll('option')).forEach((option) => {
                    optionsList.insertAdjacentHTML('beforeend', this.renderOption(option, optionIndex++));
                });
            });

            Array.from(ms.select.children).forEach((child) => {
                if (child.tagName === 'OPTION') {
                    optionsList.insertAdjacentHTML('beforeend', this.renderOption(child as HTMLOptionElement, optionIndex++));
                }
            });
        } else {
            Array.from(ms.select.options).forEach((option, index) => {
                optionsList.insertAdjacentHTML('beforeend', this.renderOption(option, index));
            });
        }

        ms.menuBody.appendChild(optionsList);

        if (ms.isMultiple) {
            const footer = this.createElement('div', 'select-footer');
            footer.innerHTML = `
                <span class="selection-count"></span>
                <button type="button" class="clear-all-btn">${ms.clearText}</button>
            `;
            ms.menu.appendChild(footer);
            ms.countSpan = ms.menu.querySelector('.selection-count');
            ms.clearAllBtn = ms.menu.querySelector('.clear-all-btn');
        }
    }

    private slideToggle(element: HTMLElement, duration = 150): void {
        element.classList.contains('open') ? this.slideUp(element, duration) : this.slideDown(element, duration);
    }

    private slideDown(element: HTMLElement, duration: number = 150): void {
        element.style.display = 'block';
        element.style.overflow = 'hidden';
        const height = element.scrollHeight;
        element.style.height = '0px';
        element.offsetHeight;
        element.style.transition = `height ${duration}ms ease`;
        element.style.height = `${height}px`;

        window.setTimeout(() => {
            Object.assign(element.style, { height: '', overflow: '', transition: '' });
            element.classList.add('open');
            this.scrollIntoViewIfNeeded(element);
        }, duration);
    }

    private scrollIntoViewIfNeeded(element: HTMLElement): void {
        // Find scrollable parent (modal body or other scroll container)
        const scrollParent = this.getScrollParent(element);

        if (!scrollParent) {
            return;
        }

        const elementRect = element.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();

        // Check if dropdown bottom is below the visible area
        const overflowBottom = elementRect.bottom - parentRect.bottom;

        if (overflowBottom > 0) {
            scrollParent.scrollTop += overflowBottom + 10; // 10px extra padding
        }
    }

    private getScrollParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;

        while (parent) {
            const { overflow, overflowY } = window.getComputedStyle(parent);

            if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
                return parent;
            }

            parent = parent.parentElement;
        }

        return null;
    }

    private slideUp(element: HTMLElement, duration: number = 150): void {
        element.style.overflow = 'hidden';
        element.style.height = `${element.scrollHeight}px`;
        element.offsetHeight;
        element.style.transition = `height ${duration}ms ease`;
        element.style.height = '0px';

        window.setTimeout(() => {
            Object.assign(element.style, { display: 'none', height: '', overflow: '', transition: '' });
            element.classList.remove('open');
        }, duration);
    }

    public bindEvents(): void {
        const { ms } = this;

        ms.toggle.addEventListener('click', () => {
            this.slideToggle(ms.menu);
            ms.toggle.classList.toggle('active');
        });

        document.addEventListener('click', (e: MouseEvent) => {
            if (!ms.wrapper.contains(e.target as Node)) {
                this.slideUp(ms.menu);
                ms.toggle.classList.remove('active');

                if (ms.isSearchable) {
                    ms.search.reset();
                    ms.menu.querySelectorAll('.form-check').forEach(el => el.classList.remove('d-none'));
                }
            }
        });

        if (ms.isMultiple && ms.showCheckboxes) {
            ms.menuBody.addEventListener('change', (e: Event) => {
                const target = e.target as HTMLInputElement;

                if (!target.classList.contains('form-check-input')) {
                    return;
                }

                if (target.checked && !ms.maxSelectedFeature.canSelectMore()) {
                    target.checked = false;
                    ms.maxSelectedFeature.showError();

                    return;
                }

                const option = ms.select.querySelector<HTMLOptionElement>(`option[value="${target.value}"]`);

                if (option) {
                    option.selected = target.checked;
                }

                ms.updateSelection();
            });

            ms.menuBody.addEventListener('click', (e: MouseEvent) => {
                const target = e.target as HTMLElement;

                if (ms.isCreatable && ms.creatableFeature.handleClick(target)) {
                    return;
                }
            });
        } else {
            ms.menuBody.addEventListener('click', (e: MouseEvent) => {
                const target = e.target as HTMLElement;

                if (ms.isCreatable && ms.creatableFeature.handleClick(target)) {
                    return;
                }

                const optionDiv = target.closest('.form-check') as HTMLElement | null;

                if (!optionDiv) {
                    return;
                }

                const nativeOption = ms.select.querySelector<HTMLOptionElement>(`option[value="${optionDiv.dataset.value}"]`);

                if (!nativeOption) {
                    return;
                }

                if (ms.isMultiple) {
                    if (!nativeOption.selected && !ms.maxSelectedFeature.canSelectMore()) {
                        ms.maxSelectedFeature.showError();

                        return;
                    }
                    nativeOption.selected = !nativeOption.selected;
                } else {
                    Array.from(ms.select.options).forEach(opt => opt.selected = false);
                    nativeOption.selected = true;
                    this.slideUp(ms.menu);
                    ms.toggle.classList.remove('active');
                }
                ms.updateSelection();
            });
        }

        ms.tagsContainer.addEventListener('click', (e: MouseEvent) => {
            const closeBtn = (e.target as HTMLElement).closest('.close-btn');
            
            if (!closeBtn) {
                return;
            }

            e.stopPropagation();
            const tag = closeBtn.closest('.selected-tag') as HTMLElement | null;
            
            if (!tag) {
                return;
            }

            const option = ms.select.querySelector<HTMLOptionElement>(`option[value="${tag.dataset.value}"]`);
            
            if (option) {
                option.selected = false;
            }
            
            ms.updateSelection();
        });

        if (typeof ms.options.onTagClick === 'function') {
            ms.tagsContainer.addEventListener('click', (e: MouseEvent) => {
                if ((e.target as HTMLElement).closest('.close-btn')) {
                    return;
                }

                const tag = (e.target as HTMLElement).closest('.selected-tag') as HTMLElement | null;
                
                if (tag) {
                    ms.options.onTagClick!(tag, tag.dataset.value, ms);
                }
            });
        }

        if (ms.isSearchable) {
            ms.search.bindTagsEvents();
        }
        
        if (ms.clearAllBtn) {
            ms.clearAllBtn.addEventListener('click', () => ms.clear());
        }
        
        if (ms.isSortable && ms.isMultiple && ms.showTags) {
            this.bindSortEvents();
        }
    }

    private bindSortEvents(): void {
        const { ms } = this;
        const container = ms.tagsContainer;
        let draggedTag: HTMLElement | null = null;
        let placeholder: HTMLSpanElement | null = null;
        let isDraggingFromHandle = false;

        container.addEventListener('mousedown', (e) => {
            const handle = (e.target as HTMLElement).closest('.drag-handle');
            
            if (!handle) {
                return;
            }

            const tag = handle.closest('.selected-tag') as HTMLElement | null;
            
            if (tag) {
                tag.draggable = true;
                isDraggingFromHandle = true;
            }
        });

        document.addEventListener('mouseup', () => {
            if (isDraggingFromHandle) {
                container.querySelectorAll<HTMLElement>('.selected-tag[draggable="true"]').forEach(tag => {
                    tag.draggable = false;
                });
                isDraggingFromHandle = false;
            }
        });

        container.addEventListener('dragstart', (e: DragEvent) => {
            if (!isDraggingFromHandle) {
                e.preventDefault();
                return;
            }

            const tag = (e.target as HTMLElement).closest('.selected-tag') as HTMLElement | null;
            
            if (!tag) {
                return;
            }

            draggedTag = tag;
            tag.classList.add('dragging');
            e.dataTransfer?.setData('text/plain', '');

            placeholder = document.createElement('span');
            placeholder.className = 'tag-placeholder';
            Object.assign(placeholder.style, { width: `${tag.offsetWidth}px`, height: `${tag.offsetHeight}px` });
        });

        container.addEventListener('dragover', (e: DragEvent) => {
            e.preventDefault();
            
            if (!draggedTag || !placeholder) {
                return;
            }

            const afterElement = this.getDragAfterElement(container, e.clientX);
            afterElement ? container.insertBefore(placeholder, afterElement) : container.appendChild(placeholder);
        });

        container.addEventListener('drop', (e: DragEvent) => {
            e.preventDefault();
            
            if (draggedTag && placeholder?.parentNode) {
                placeholder.parentNode.insertBefore(draggedTag, placeholder);
            }
        });

        container.addEventListener('dragend', () => {
            draggedTag?.classList.remove('dragging');
            placeholder?.remove();
            draggedTag = null;
            placeholder = null;
            this.syncTagOrder();
        });
    }

    private getDragAfterElement(container: HTMLElement, x: number): HTMLElement | undefined {
        const draggables = Array.from(container.querySelectorAll<HTMLElement>(`.selected-tag.from-${this.ms.instanceId}:not(.dragging)`));

        return draggables.reduce<{ offset: number; element?: HTMLElement }>((closest, child) => {
            const box = child.getBoundingClientRect();
            const offset = x - box.left - box.width / 2;
            
            return (offset < 0 && offset > closest.offset) ? { offset, element: child } : closest;
        }, { offset: Number.NEGATIVE_INFINITY }).element;
    }

    private syncTagOrder(): void {
        const { ms } = this;
        const orderedValues = this.getOrderedTagValues();

        ms.wrapper.dataset.selectedOrder = JSON.stringify(orderedValues);
        ms.updateHiddenInputs?.(orderedValues);

        ms.select.dispatchEvent(new CustomEvent('orderChange', {
            bubbles: true,
            detail: { orderedValues }
        }));

        ms.options.onOrderChange?.(orderedValues, ms);
    }

    public getOrderedTagValues(): string[] {
        const { ms } = this;

        return Array.from(ms.tagsContainer.querySelectorAll<HTMLElement>(`.selected-tag.from-${ms.instanceId}`))
            .map(tag => tag.dataset.value || '');
    }

    public updateSelection(): void {
        const { ms } = this;
        const selectedOptions = Array.from(ms.select.selectedOptions);
        const count = selectedOptions.length;

        if (ms.isMultiple) {
            // Capture the existing tag order BEFORE wiping. Empty means this is
            // the first render — fall back to `data-selected-order` on the
            // <select> (set by PHP from the saved sort_order). Subsequent
            // renders preserve whatever order the user dragged the tags into.
            const existingTags = Array.from(
                ms.tagsContainer.querySelectorAll<HTMLElement>(`.selected-tag.from-${ms.instanceId}`)
            );
            const previousOrder = existingTags.map((el) => el.dataset.value || '');

            existingTags.forEach((el) => el.remove());

            const countText = ms.countText.replace('{count}', String(count));
            ms.displayText.textContent = count > 0 ? countText : ms.placeholder;

            if (ms.countSpan) {
                ms.countSpan.textContent = countText;
            }

            if (ms.clearAllBtn) {
                ms.clearAllBtn.style.display = count > 0 ? '' : 'none';
            }

            if (ms.showTags) {
                const orderedOptions = this.orderSelectedOptions(selectedOptions, previousOrder);

                orderedOptions.forEach((option) => {
                    const tag = this.createElement('span', `selected-tag from-${ms.instanceId}`);
                    tag.dataset.value = option.value;

                    if (typeof ms.options.onTagClick === 'function') {
                        tag.classList.add('clickable');
                    }

                    const dragHandle = ms.isSortable ? `
                        <span class="drag-handle">
                            <svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor" style="pointer-events: none;">
                                <circle cx="1.5" cy="1.5" r="1"/><circle cx="4.5" cy="1.5" r="1"/>
                                <circle cx="1.5" cy="5" r="1"/><circle cx="4.5" cy="5" r="1"/>
                                <circle cx="1.5" cy="8.5" r="1"/><circle cx="4.5" cy="8.5" r="1"/>
                            </svg>
                        </span>
                    ` : '';

                    tag.innerHTML = `
                        ${dragHandle}
                        <span class="tag-text">${option.text}</span>
                        <button type="button" class="close-btn" aria-label="Remove">&times;</button>
                    `;
                    ms.tagsContainer.appendChild(tag);
                });

                ms.options.onTagsRendered?.(ms.tagsContainer, ms);
                
                if (ms.isSortable) {
                    this.syncTagOrder();
                }
            }
        } else {
            const selectedOption = selectedOptions[0];
            ms.displayText.textContent = selectedOption?.text || ms.placeholder;
            ms.toggle.classList.toggle('single-select-active', !!selectedOption);
        }

        ms.menuBody.querySelectorAll<HTMLElement>('.form-check').forEach((el) => {
            const option = ms.select.querySelector<HTMLOptionElement>(`option[value="${el.dataset.value}"]`);
            const isSelected = !!option?.selected;
            const input = el.querySelector<HTMLInputElement>('.form-check-input');

            el.classList.toggle('selected', isSelected);

            if (input) {
                input.checked = isSelected;
            }
        });
    }

    /**
     * Returns selectedOptions in the desired order.
     *
     * - If `previousOrder` is non-empty (i.e. we're re-rendering and the user
     *   has already dragged tags into some order), preserve that order.
     * - Otherwise, on first render, fall back to the `data-selected-order`
     *   attribute on the <select> (set by PHP from the saved sort_order).
     * - Options not in either order are appended at the end (covers new
     *   selections made via the menu after initial render).
     */
    private orderSelectedOptions(
        selectedOptions: HTMLOptionElement[],
        previousOrder: string[]
    ): HTMLOptionElement[] {
        const { ms } = this;
        let desiredOrder: string[] = previousOrder;

        if (desiredOrder.length === 0 && ms.isSortable && ms.select.dataset.selectedOrder) {
            try {
                desiredOrder = JSON.parse(ms.select.dataset.selectedOrder).map(String);
            } catch {
                desiredOrder = [];
            }
        }

        if (desiredOrder.length === 0) {
            return selectedOptions;
        }

        const byValue: Record<string, HTMLOptionElement> = {};

        selectedOptions.forEach((option) => {
            byValue[option.value] = option;
        });

        const ordered: HTMLOptionElement[] = [];

        desiredOrder.forEach((value) => {
            const option = byValue[value];

            if (option) {
                ordered.push(option);
                delete byValue[value];
            }
        });

        // Append any selected options not present in desiredOrder
        Object.values(byValue).forEach((option) => {
            ordered.push(option);
        });

        return ordered;
    }
}